import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UPGRADE_MAP, useUpgradeCheckout } from "@/components/upgrade-modal";
import { authClient } from "@/lib/api/auth-client";
import { cn } from "@/lib/utils";

type UsageWidgetVariant = "sidebar" | "card";

interface UsageWidgetProps {
	variant: UsageWidgetVariant;
	className?: string;
}

export function useUsageQuery() {
	return useQuery({
		queryKey: ["billing", "usage"],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/billing/usage");
			if (error) throw new Error("Failed to fetch usage");
			return data;
		},
		staleTime: 30_000, // 30 seconds
	});
}

function getPlanDisplayName(plan: string): string {
	switch (plan) {
		case "free":
			return "Free";
		case "pro":
			return "Pro";
		case "max_5x":
			return "Max 5x";
		case "max_20x":
			return "Max 20x";
		default:
			return plan;
	}
}

function getResetDate(periodStart: string): Date {
	const periodEnd = new Date(periodStart);
	periodEnd.setDate(periodEnd.getDate() + 30);
	return periodEnd;
}

function getDaysUntilReset(periodStart: string): number {
	const diff = getResetDate(periodStart).getTime() - Date.now();
	return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatResetDateTime(periodStart: string): string {
	return getResetDate(periodStart).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function UsageWidget({ variant, className }: UsageWidgetProps) {
	const { data: usage, isLoading, error } = useUsageQuery();
	const { checkout, isLoading: checkoutLoading } = useUpgradeCheckout();

	if (isLoading || error || !usage) {
		if (variant === "sidebar") {
			return (
				<div className={cn("flex justify-center px-2 py-3", className)}>
					<Spinner className="size-4" />
				</div>
			);
		}
		return null;
	}
	const costLimitHit = usage.cost_limit_hit ?? false;
	const percentage = costLimitHit ? 100 : Math.min(100, (usage.runs_used / usage.runs_limit) * 100);
	const planName = getPlanDisplayName(usage.plan);
	const daysUntilReset = getDaysUntilReset(usage.period_start);
	const isAtLimit = usage.runs_used >= usage.runs_limit || costLimitHit;
	const isNearLimit = percentage >= 80;
	const upgradeTarget = UPGRADE_MAP[usage.plan];

	if (variant === "sidebar") {
		return (
			<Link
				to="/billing"
				className={cn("block rounded-md px-2 py-3 transition-colors hover:bg-accent", className)}
			>
				<div className="flex items-center justify-between text-sm">
					<Progress
						value={percentage}
						className={cn("h-1.5 flex-1", "[&>[data-state=complete]]:bg-primary", {
							"[&>div]:bg-destructive": percentage >= 100,
							"[&>div]:bg-orange-500": percentage >= 90 && percentage < 100,
							"[&>div]:bg-yellow-500": percentage >= 70 && percentage < 90,
						})}
					/>
					<span className="ml-2 tabular-nums text-muted-foreground">
						{costLimitHit && usage.runs_used < usage.runs_limit
							? "Limit reached"
							: `${usage.runs_used}/${usage.runs_limit}`}
					</span>
				</div>
				<p className="mt-1 text-xs text-muted-foreground">
					{planName} {isAtLimit && " • Upgrade for more"}
				</p>
			</Link>
		);
	}

	// Card variant
	return (
		<Card className={cn("border-border/70", className)}>
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-medium">Usage This Month</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<div>
					<div className="mb-2 flex items-center justify-between text-sm">
						<span className="text-muted-foreground">Tasks completed</span>
						<span className="font-medium tabular-nums">
							{usage.runs_used} / {usage.runs_limit}
						</span>
					</div>
					<Progress
						value={percentage}
						className={cn("h-2", {
							"[&>div]:bg-destructive": percentage >= 100,
							"[&>div]:bg-orange-500": percentage >= 90 && percentage < 100,
							"[&>div]:bg-yellow-500": percentage >= 70 && percentage < 90,
						})}
					/>
				</div>

				<div className="flex items-center justify-between text-sm">
					<span className="text-muted-foreground">
						{planName} &middot;{" "}
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="cursor-default">
									Resets in {daysUntilReset} {daysUntilReset === 1 ? "day" : "days"}
								</span>
							</TooltipTrigger>
							<TooltipContent variant="subtle">
								{formatResetDateTime(usage.period_start)}
							</TooltipContent>
						</Tooltip>
					</span>
					{costLimitHit && usage.runs_used < usage.runs_limit ? (
						<span className="text-destructive font-medium">Fair usage limit reached</span>
					) : (
						["past_due", "unpaid", "incomplete_expired"].includes(
							usage.subscription_status ?? ""
						) && <span className="text-destructive">Payment issue</span>
					)}
				</div>

				{(isNearLimit || isAtLimit) && upgradeTarget && (
					<Button
						className="w-full"
						variant={isAtLimit ? "default" : "outline"}
						disabled={checkoutLoading}
						onClick={() => checkout(upgradeTarget.planId)}
					>
						{checkoutLoading ? (
							<Spinner className="size-4" />
						) : isAtLimit ? (
							"Upgrade to continue"
						) : (
							"Upgrade for more tasks"
						)}
					</Button>
				)}
			</CardContent>
		</Card>
	);
}
